import React, { Component } from 'react';
import ReportPage from './ReportPage';

class MethodologyPages extends Component {
    render() {
        return [
            <ReportPage key="meth1" title="Methodology Appendix" pageNumber={this.props.pageNumber} className="methodology" addBreak={true}>
                <p className="clearfix">
                    <span className="d-block aside float-right">
                        Longevity risk—the risk of outliving one’s savings in retirement—is the key concern for most participants.
                    </span>
                    Target-Date Blueprint from American Century incorporates an in-depth analysis of target-date series risk to provide plan sponsors with a thoughtful 
                    approach to best match the target-date fund (TDF) option to the participants’ risk preferences, savings behavior and investment goals.  
                    Based on an evaluation of each TDF glide path and underlying allocations, the tool assesses the relative riskiness of each series across a 
                    variety of risk measures.  Taken together, the eight risk measures form the basis for a deeper understanding of how <b>longevity risk</b>—the risk of 
                    outliving wealth in retirement—is managed in each TDF series.
                </p>                

                <p className="">
                    <b className="text-blue">What types of analysis did you do?</b>
                    American Century conducted proprietary research on retirement investing risks to determine the eight risk measurements in the tool.  These eight measures can be grouped into four categories: Growth, Market, Macro Scenario, and Income Horizon risks.  For more background on the calculation and sensibility behind these risks, please check the American Century Investments website for our white papers on these topics.                    
                </p>

                <p>
                Optimal Glide Path for savings/risk profile based on original research described in "On Optimal Allocations of Target-Date Funds" by Radu Gabudean, Francisco Gomes, Alexander Michaelides, and Yuxin Zhang appears in the Fall 2021 issue of The Journal of Retirement, © 2021, PMR. All rights reserved.<a target="_blank" href="https://www.pm-research.com/">https://www.pm-research.com/</a> Link to paper: <a target="_blank" href="https://www.americancentury.com/insights/optimal-glide-paths-new-research">https://www.americancentury.com/insights/optimal-glide-paths-new-research</a>
                
                </p>


            </ReportPage>,
            <ReportPage key="meth2" title="Methodology Appendix" pageNumber={this.props.pageNumber + 1} className="methodology" addBreak={true} >
                <table className="table table-risk">
                    <thead>
                        <tr>
                            <th>&nbsp;</th>
                            <th>Growth Risk</th>
                            <th>Market Risk</th>
                            <th>Macro<div>Scenario Risk</div></th>
                            <th>Income<div>Horizon Risk</div></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Description</td>
                            <td>Risk of not meeting an expected return goal</td>
                            <td>Risk of a shortfall caused by the variability and timing of returns</td>
                            <td>The risk that changes in broad economic forces erode gains</td>
                            <td>The risk that income won’t last over a long retirement</td>
                        </tr>
                        <tr>
                            <td>Risk Measures In Group</td>
                            <td>
                                • Growth
                            </td>
                            <td>
                                <div>• Long-run volatility</div>
                                <div>• Sequence-of-Returns</div>
                                <div>• Max Drawdown over 3-Years</div>
                            </td>
                            <td>
                                <div>• Interest rate</div>
                                <div>• Inflation</div>
                                <div>• Currency</div>
                            </td>
                            <td>
                                • Income Horizon
                            </td>
                        </tr>
                        <tr>
                            <td>Types of Data Used</td>
                            <td>
                                • Glide path wealth-weighted % equity
                            </td>
                            <td>
                                <div>• Capital market risk assumptions for equity/non-equity</div>
                                <div>• Glide path slope and equity level around retirement</div>
                            </td>
                            <td>
                                • Capital market assumptions for asset class sensitivities to interest rate, inflation, and currency/fx fluctuation
                            </td>
                            <td>
                                • Simulated max sustained withdrawal over 30 years with a 90% success probability
                            </td>
                        </tr>
                    </tbody>
                </table>
            </ReportPage>,
            <ReportPage key="meth3" title="Methodology Appendix" pageNumber={this.props.pageNumber + 2} className="methodology" addBreak={true} >
                <p className="">
                    <b className="text-blue">How is the TDF risk assessment aligned to each plan profile?</b>
                    There are 12 “risk profiling” questions (Questions 8-19) in the Questionnaire. These 12 questions are linked to a set of weights for each risk measure that reflect the relative importance as indicated by the user’s responses.  Points are given to each risk measure based on the response provided by the user and the total points awarded per question. At the end of the survey, the points are totaled and a final customized risk-weighting scheme is allocated across each of the 8 measures.  Greater weight will be allocated to the risks that are most aligned to the user’s risk profile as indicated by the provided responses. 
                </p>
                <p>
                
               
               Separately, each of the more than 40 series included in the tool has been assigned a “risk ranking” across the 8 risk measures, 
               ranking each series for its potential ability to manage each risk compared to the universe of TDFs evaluated.  A total custom-
               weighted ranking for each series is calculated by applying the user’s risk-weighting scheme to each TDF’s risk rankings and 
               summing to get the customized ranking for each TDF.  The report output generates the list of all TDFs separated into four quartiles, 
               with Quartile 1 containing the “most suitable” target-date series, or those receiving the highest custom-weighted rankings.  
               This breakdown continues to the fourth quartile based on the customized rankings, which would be considered the “least suitable”
                group of TDFs. 
               
               
                
                 
                </p>
                <p>
                    For example, if the responses provided in the questionnaire align more with a consistent emphasis on capital growth over managing volatility, TDFs that rank highly on managing Growth Risk (i.e., targeting higher expected returns) will generally appear further up in the customized list of most suitable to least suitable TDF series for that plan.
                </p>
            </ReportPage>,
            <ReportPage key="meth4" title="Methodology Appendix" pageNumber={this.props.pageNumber + 3} className="methodology" addBreak={true} >
                <p>
                    <b className="text-blue">Many of the questions and responses appear straightforward to interpret, but tell me about some of the less obvious ones. How did you interpret the existence of a DB plan, or the predominant age of participants, for example?</b>
                    All the information gathered in the questionnaire requires some informed judgment about the relative risk preferences indicated by the responses.  In most cases the link between response and risk emphasis is straightforward, while in other cases less so.  Here we provide the details of our assumptions.  
                </p> 
            <table className="table table-sm table-bordered table-questions">
                    <tbody>
                        <tr>
                            <td>Question</td>
                            <td>Interpretation</td>
                        </tr>
                        <tr>
                            <td>8-9. What are the ages of the greatest number of plan participants? Among which group of participants do the highest concentration of  assets lie?</td>
                            <td>
                                •   Plans which skew to the youngest category of participants and asset concentration (less than 35 years old) have a higher degree of emphasis on growth of capital than the average plan.<br/>
                                •   Plans which skew to the oldest category of participants and asset concentration (greater than 50 years old) have a higher degree of emphasis on reducing the potential for large losses than the average plan.<br/>
                                •   Plans in the middle category have a balanced emphasis on capital growth and reducing the potential for large losses.
                            </td>
                        </tr>
                    </tbody>
                </table>
               
        </ReportPage>,
        <ReportPage key="meth4" title="Methodology Appendix" pageNumber={this.props.pageNumber + 4} className="methodology" addBreak={true} >
            <table className="table table-sm table-bordered table-questions">
                    <tbody>
                        <tr>
                            <td>Question</td>
                            <td>Interpretation</td>
                        </tr>
                        <tr>
                            <td>10.  At what age do you expect most plan participants to retire</td>
                            <td>
                                •   Participants who are expected to retire earlier than full retirement age (under 62) have a shorter time to save and thus require a higher emphasis on growth to reduce longevity risk. <br/>
                                •   Participants who are expected to retire beyond full retirement age (67 or older) will have more years of savings and more income security. These participants should emphasize mitigating market shocks and should be less concerned about above-average growth potential.<br/>
                                •   Plans in the middle category have a balanced emphasis on capital growth and reducing the potential for large losses.
                            </td>
                        </tr>
                        <tr>
                            <td>11. Do plan participants participate in a traditional defined benefit plan?</td>
                            <td>
                            •   If yes, we assume that participants utilize the DC plan as a supplemental savings plan and can emphasize capital growth with less emphasis on reducing the potential for large losses.<br/>
                            •  Plans with no DB plan have a somewhat greater focus on reducing large losses pre-retirement and increasing income potential in retirement.
                            </td>
                            </tr>
                        <tr>
                            <td>12. Which of the below is closest to the average salary level of defined contribution participants who invest in TDFs?</td>
                            <td>
                                •   Higher incomes are associated with greater income needs in retirement but also lower income replacement from Social Security payments.  Therefore, higher incomes are linked to greater emphasis on reducing the potential for losses and a greater need for income from the employees’ own savings.<br/>
                                •   Lower incomes are associated with higher income replacement from Social Security payments.  However, the potential for rising costs in retirements indicate the need for greater capital growth from the employees’ own savings to supplement the lower dollar value of Social Security pensions.
                            </td>
                        </tr>
                    </tbody>
                </table>  
        </ReportPage>,
        <ReportPage key="meth5" title="Methodology Appendix" pageNumber={this.props.pageNumber + 5} className="methodology" addBreak={true} >
            <table className="table table-sm table-bordered table-questions">
                    <tbody>
                        <tr>
                            <td>Question</td>
                            <td>Interpretation</td>
                        </tr>
                        <tr>
                            <td>13. What is the turnover rate for employees?</td>
                            <td>
                                •   Lower than average turnover rates indicate the capacity of employees to take on more risk in exchange for higher expected growth given the longer nature of their expected investment horizon within the same QDIA option.<br/>
                                •   Higher turnover rates reduce the capacity of participants to bear the potential for large losses given the shorter nature of their expected investment horizon.
                            </td>
                        </tr>
                        <tr>
                            <td>14. What is the average contribution rate (including match)?**</td>
                            <td>
                                •   Higher contribution rates support greater emphasis on mitigating market risks for income certainty<br/>
                                •   Average contribution rates support average risk-taking<br/>
                                •   Lower contribution rates support greater emphasis on capital growth for income certainty
                            </td>
                        </tr>
                        <tr>
                            <td>15. What do the majority of participants do with their plan assets after their retirement date?</td>
                            <td>
                                •   A majority remaining in the plan after retirement suggests a greater focus on income solutions in the plan<br/>
                                •   Plans utilizing guaranteed income solutions may seek greater principal protection of liquid assets as retirement nears<br/>
                                •   A majority leaving the plan after retirement reduces the need for income solutions in-plan
                            </td>
                        </tr>     
                        <tr>
                            <td>16.  Which of the following best fits the plan’s objectives?</td>
                            <td>
                                •   A greater focus on long-term return and acceptance of higher volatility translates to an emphasis on managing Growth risk<br/>
                                •   A moderate risk/return profile balances Growth risk with Market risk<br/>
                                •   An emphasis on smoothing volatility and limiting losses translates to a higher weight on Market risk
                            </td>
                        </tr>                                                                                                                    
                </tbody>
            </table>              
        </ReportPage>,
        <ReportPage key="meth6" title="Methodology Appendix" pageNumber={this.props.pageNumber + 6} className="methodology" addBreak={true} >
            <table className="table table-sm table-bordered table-questions">
                <tbody>
                    <tr>
                        <td>Question</td>
                        <td>Interpretation</td>
                    </tr>
                    <tr>
                        <td>17.  How important is it for the target-date strategy to serve as an income solution for retirees remaining in the plan?</td>
                        <td>
                            •   A high importance on income will place a higher weight on Income Horizon risk, while an answer of “Not Important” will place zero weight on Income Horizon.
                        </td>
                    </tr> 
                    <tr>
                        <td>18.  What does the majority of plan participants level of total savings suggest for their target-date strategy?</td>
                        <td>
                            •   Generally, participants with higher accumulated wealth place more emphasis on capital preservation. However, the answers allow flexibility to answer as best suits the demographics of the plan. <br/>
                            •   A greater focus on growth translates to an emphasis on managing Growth risk<br/>
                            •   An equal emphasis on growth/preservation balances Growth risk with Market risk<br/>
                            •   An emphasis on capital preservation translates to a higher weight on Market and Income Horizon risk
                        </td>
                    </tr>   
                    <tr>
                        <td>19. Which statement best describes the likely change in behavior for an employee nearing retirement following a stock market decline of 20% or more?**</td>
                        <td>
                            •   A greater likelihood to buy stocks after a market decline increases the weight on Growth risk<br/>
                            •   A greater likelihood to move out of stocks after a market decline increase the weight on Market risk factors<br/>
                            •   No change in participant behavior signals a balance between Growth and Market risk.
                        </td>
                    </tr>  
                    <tr>
                        <td colspan="2">**Responses to questions 14 & 19 influence the selection of the optimal glide path in the analysis for the plan.</td>
                
                    </tr>  
                </tbody>
            </table>
        </ReportPage>
        ];
    }
}

export default MethodologyPages;
